'use client';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Personal HubSpot Meetings scheduling link, e.g.
// "https://meetings.hubspot.com/js-martinez". Set in .env.local.
export const HUBSPOT_MEETINGS_LINK =
  process.env.NEXT_PUBLIC_HUBSPOT_MEETINGS_LINK ?? '';

// Ensure the scheduler renders inline (chromeless) inside our modal iframe.
function embedUrl(link: string): string {
  if (!link) return '';
  const sep = link.includes('?') ? '&' : '?';
  return `${link}${sep}embed=true`;
}

interface HubSpotMeetingsProps {
  label: string;
  className?: string;
}

/**
 * "Book a Discovery Call" trigger that opens the HubSpot Meetings scheduler in a
 * modal. Bookings land natively in the HubSpot CRM (contact + meeting), so this
 * replaces the previous Cal.com embed.
 *
 * We render the HubSpot scheduler as a plain iframe (embed=true) rather than
 * relying on HubSpot's MeetingsEmbedCode.js auto-scan, which does not re-run on
 * client-side navigation and would leave the modal blank on repeat visits.
 */
export function HubSpotMeetings({ label, className = '' }: HubSpotMeetingsProps) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Close on Escape + lock body scroll while the modal is open.
  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!HUBSPOT_MEETINGS_LINK) return null;

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={className}>
        {label}
      </button>

      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label={label}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-999 flex items-center justify-center bg-black/70 p-4"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="relative h-[85vh] w-full max-w-3xl overflow-hidden rounded-xl bg-white shadow-2xl"
            >
              <button
                ref={closeRef}
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/5 text-gray-600 transition-colors hover:bg-black/10"
              >
                ✕
              </button>

              {!loaded && (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-400">
                  Loading calendar…
                </div>
              )}

              <iframe
                title={label}
                src={embedUrl(HUBSPOT_MEETINGS_LINK)}
                onLoad={() => setLoaded(true)}
                className="h-full w-full border-0"
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
